import { useState, useEffect, useRef, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import Button from 'ui/components/buttons/Button/'
import Icon from 'ui/components/Icon'
import { ButtonWrap, Menu } from './styled'

const ButtonMoreActions = ({
  menuPosition = 'right',
  onOpenChange,
  children,
}) => {
  const wrapRef = useRef(null)
  const parentRowRef = useRef(null)
  const menuRef = useRef(null)

  const [openMenu, setOpenMenu] = useState(false)
  const [coords, setCoords] = useState(null)
  const [menuWidth, setMenuWidth] = useState(240)

  useEffect(() => {
    parentRowRef.current = wrapRef.current?.closest('[data-row]') || null
    return () => {
      parentRowRef.current?.removeAttribute('data-menu-open')
    }
  }, [])

  const updateAnchor = () => {
    if (!wrapRef.current) return
    const r = wrapRef.current.getBoundingClientRect()
    setCoords({ top: r.bottom, left: r.left, width: r.width, height: r.height })
  }

  useLayoutEffect(() => {
    if (!openMenu) return
    updateAnchor()

    requestAnimationFrame(() => {
      if (menuRef.current) {
        const mr = menuRef.current.getBoundingClientRect()
        if (mr.width) setMenuWidth(mr.width)
      }
    })

    const onResize = () => updateAnchor()
    const onScroll = () => updateAnchor()
    window.addEventListener('resize', onResize, true)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('resize', onResize, true)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [openMenu])

  useEffect(() => {
    onOpenChange && onOpenChange(openMenu)
  }, [openMenu, onOpenChange])

  useEffect(() => {
    if (!openMenu) return
    const onDown = (e) => {
      const inTrigger = wrapRef.current && wrapRef.current.contains(e.target)
      const inMenu = menuRef.current && menuRef.current.contains(e.target)
      if (!inTrigger && !inMenu) closeMenu()
    }
    const onKey = (e) => {
      if (e.key === 'Escape') closeMenu()
    }
    document.addEventListener('mousedown', onDown, true)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown, true)
      document.removeEventListener('keydown', onKey)
    }
  }, [openMenu])

  const openMenuFn = () => {
    setOpenMenu(true)
    parentRowRef.current?.setAttribute('data-menu-open', 'true')
  }
  const closeMenu = () => {
    setOpenMenu(false)
    parentRowRef.current?.removeAttribute('data-menu-open')
  }

  const calcLeft = () => {
    if (!coords) return 0
    const vw = window.innerWidth

    let left =
      menuPosition === 'left'
        ? coords.left
        : coords.left + coords.width - menuWidth

    const minLeft = 8
    const maxLeft = vw - menuWidth - 8
    if (left < minLeft) left = minLeft
    if (left > maxLeft) left = maxLeft

    return Math.round(left)
  }

  return (
    <>
      <ButtonWrap ref={wrapRef}>
        <Button
          type="button"
          theme="colorNone"
          icon={<Icon name="more" size={16} />}
          onClick={() => (openMenu ? closeMenu() : openMenuFn())}
        />
      </ButtonWrap>

      {openMenu &&
        coords &&
        createPortal(
          <Menu
            ref={menuRef}
            role="menu"
            data-open="true"
            data-portal="true"
            menuPosition={menuPosition}
            style={{
              position: 'fixed',
              top: Math.round(coords.top) + 6,
              left: calcLeft(),
            }}
          >
            {children({ close: closeMenu })}
          </Menu>,
          document.body
        )}
    </>
  )
}

export default ButtonMoreActions
